/**
 * Generative-UI runtime, the load-bearing primitive of S5.
 *
 * The Aiko agent emits a JSON schema describing a screen composition,
 * where each node names ONE primitive id and a payload bound to the
 * agent's tool-call data. This module consumes the schema and mounts
 * the primitives. The agent never writes JSX; the runtime never picks
 * data, both responsibilities are kept separate per pointer-not-payload
 * (contract:3c2dd353).
 *
 * Schema:
 *   { primitive: "kpi", props: KpiProps }
 *   { primitive: "row", children: Node[] }
 *   { primitive: "col", children: Node[] }
 *   { primitive: "card", title?, subtitle?, children: Node[] }
 *   { primitive: "table", props: TableProps<unknown> }
 *   { primitive: "chart", props: ChartProps }
 *   { primitive: "timeline", props: { events: TimelineEvent[] } }
 *   { primitive: "kanban", props: KanbanColumn<unknown>[] }
 *   { primitive: "drilldown", props: { items: DrilldownItem[] } }
 *   { primitive: "form", props: FormProps }    // confirm-before-irreversible
 *   { primitive: "chip", props: ChipProps }
 *   { primitive: "empty", props: EmptyProps }
 *   { primitive: "text", text: string, tone?: "ink" | "soft" }
 *   { primitive: "heading", text: string, level?: 1 | 2 | 3 }
 */

import { Card, type CardProps } from "./Card";
import { Kpi, KpiRow, type KpiProps } from "./Kpi";
import { Table, type TableProps } from "./Table";
import { Chart, type ChartProps } from "./Chart";
import { Form, type FormProps } from "./Form";
import { Timeline, type TimelineEvent } from "./Timeline";
import { Chip, type ChipProps } from "./Chip";
import { Empty, type EmptyProps } from "./Empty";
import { Drilldown, type DrilldownItem } from "./Drilldown";
import { Kanban, type KanbanColumn } from "./Kanban";
import { type ReactNode } from "react";

export type RenderNode =
  | { primitive: "card"; props?: Omit<CardProps, "children">; children?: ReadonlyArray<RenderNode> }
  | { primitive: "row"; children: ReadonlyArray<RenderNode>; gap?: number }
  | { primitive: "col"; children: ReadonlyArray<RenderNode>; gap?: number }
  | { primitive: "kpiRow"; children: ReadonlyArray<RenderNode> }
  | { primitive: "kpi"; props: KpiProps }
  | { primitive: "table"; props: TableProps<Record<string, unknown>> }
  | { primitive: "chart"; props: ChartProps }
  | { primitive: "timeline"; props: { events: ReadonlyArray<TimelineEvent> } }
  | { primitive: "kanban"; props: { columns: ReadonlyArray<KanbanColumn<unknown>> } }
  | { primitive: "drilldown"; props: { items: ReadonlyArray<DrilldownItem> } }
  | { primitive: "form"; props: FormProps }
  | { primitive: "chip"; props: ChipProps }
  | { primitive: "empty"; props: EmptyProps }
  | { primitive: "text"; text: string; tone?: "ink" | "soft" }
  | { primitive: "heading"; text: string; level?: 1 | 2 | 3 };

const GAP = (n?: number) => `gap-${n ?? 3}`;
const HEADING_CLS: Record<1 | 2 | 3, string> = {
  1: "font-display text-3xl text-ink",
  2: "font-display text-2xl text-ink",
  3: "font-display text-lg text-ink",
};

export function Render({ node: raw }: { node: RenderNode | Record<string, unknown> }): ReactNode {
  // Schema tolerance — LLMs do not always emit our canonical field names.
  // Accept {type|primitive}, {text|content}, {props|...rest}, {data,columns}→{rows,columns}.
  const node = normalize(raw as Record<string, unknown>) as RenderNode;
  switch (node.primitive) {
    case "card":
      return (
        <Card {...node.props}>
          {node.children?.map((c, i) => <Render key={i} node={c} />)}
        </Card>
      );

    case "row":
      return (
        <div className={`grid grid-flow-col auto-cols-fr ${GAP(node.gap)}`}>
          {node.children.map((c, i) => <Render key={i} node={c} />)}
        </div>
      );

    case "col":
      return (
        <div className={`grid grid-cols-1 ${GAP(node.gap)}`}>
          {node.children.map((c, i) => <Render key={i} node={c} />)}
        </div>
      );

    case "kpiRow":
      return (
        <KpiRow>
          {node.children.map((c, i) => <Render key={i} node={c} />)}
        </KpiRow>
      );

    case "kpi":      return <Kpi {...node.props} />;
    case "table":    return <Table {...node.props} />;
    case "chart":    return <Chart {...node.props} />;
    case "timeline": return <Timeline {...node.props} />;
    case "kanban":   return <Kanban {...node.props} />;
    case "drilldown":return <Drilldown {...node.props} />;
    case "form":     return <Form {...node.props} />;
    case "chip":     return <Chip {...node.props} />;
    case "empty":    return <Empty {...node.props} />;

    case "heading": {
      const level = node.level ?? 2;
      const cls = HEADING_CLS[level];
      if (level === 1) return <h1 className={cls}>{node.text}</h1>;
      if (level === 3) return <h3 className={cls}>{node.text}</h3>;
      return <h2 className={cls}>{node.text}</h2>;
    }

    case "text":
      return (
        <p className={node.tone === "soft" ? "text-ink-soft" : "text-ink"}>
          {node.text}
        </p>
      );
  }
}

export function RenderTree({ tree }: { tree: ReadonlyArray<RenderNode | Record<string, unknown>> }) {
  return (
    <div className="space-y-4">
      {tree.map((n, i) => <Render key={i} node={n as RenderNode} />)}
    </div>
  );
}

/**
 * LLM-friendly schema normalizer. Accepts loose shapes and rewrites to
 * the canonical RenderNode discriminator.
 */
function normalize(n: Record<string, unknown>): Record<string, unknown> {
  if (!n || typeof n !== "object") return n;
  const out: Record<string, unknown> = { ...n };
  if (!out.primitive && typeof out.type === "string") {
    out.primitive = out.type;
    delete out.type;
  }
  // text/content alias
  if (typeof out.content === "string" && out.text === undefined) {
    out.text = out.content;
    delete out.content;
  }
  // table: many shapes - {data,columns} | {rows,columns} | {headers,rows}
  if (out.primitive === "table" && !out.props) {
    const rawRows = (out.rows ?? out.data ?? []) as unknown[];
    let columns = out.columns as Array<{ key: string; header: string }> | undefined;
    let rows: Array<Record<string, unknown>> = [];

    if (Array.isArray(out.headers) && Array.isArray(rawRows)) {
      // {headers: ["A","B"], rows: [["a1","b1"],...]}
      const headers = out.headers as string[];
      columns = headers.map((h) => ({ key: h, header: h }));
      rows = (rawRows as unknown[][]).map((r) =>
        Object.fromEntries(headers.map((h, i) => [h, r[i]])),
      );
    } else if (Array.isArray(rawRows) && rawRows.length && Array.isArray(rawRows[0])) {
      // rows-of-arrays without headers — synth ints
      const len = (rawRows[0] as unknown[]).length;
      columns ??= Array.from({ length: len }, (_, i) => ({ key: String(i), header: `col${i}` }));
      rows = (rawRows as unknown[][]).map((r) =>
        Object.fromEntries(r.map((v, i) => [String(i), v])),
      );
    } else {
      // rows-of-objects
      rows = rawRows as Array<Record<string, unknown>>;
      if (!columns && rows.length) {
        columns = Object.keys(rows[0]).map((k) => ({ key: k, header: k }));
      }
    }

    out.props = { rows, columns: columns ?? [] };
    delete out.rows; delete out.data; delete out.columns; delete out.headers;
  }
  // kpi: {label, value} (flat) → {props: {label, value, delta?}}
  if (out.primitive === "kpi" && !out.props && out.label !== undefined) {
    out.props = { label: out.label, value: out.value, delta: out.delta, hint: out.hint };
    delete out.label; delete out.value; delete out.delta; delete out.hint;
  }
  // card: {title, subtitle, tone} flat → {props}
  if (out.primitive === "card" && !out.props && (out.title || out.subtitle || out.tone)) {
    out.props = { title: out.title, subtitle: out.subtitle, tone: out.tone };
    delete out.title; delete out.subtitle; delete out.tone;
  }
  // chip: {tone, size, label/text} flat → {props: {tone, size, children}}
  if (out.primitive === "chip" && !out.props && (out.label || out.text || out.tone)) {
    out.props = { tone: out.tone, size: out.size, children: out.label ?? out.text };
    delete out.label; delete out.tone; delete out.size;
  }
  // chart: {kind, points} flat → {props}
  if (out.primitive === "chart" && !out.props && Array.isArray(out.points)) {
    out.props = { kind: out.kind ?? "bar", points: out.points, caption: out.caption };
    delete out.points; delete out.kind; delete out.caption;
  }
  // timeline: {events} flat → {props}
  if (out.primitive === "timeline" && !out.props && Array.isArray(out.events)) {
    out.props = { events: out.events };
    delete out.events;
  }
  // empty: {title, body, action} flat → {props}
  if (out.primitive === "empty" && !out.props && (out.title || out.body)) {
    out.props = { title: out.title, body: out.body, action: out.action };
    delete out.title; delete out.body; delete out.action;
  }
  // recurse into children
  if (Array.isArray(out.children)) {
    out.children = (out.children as Array<Record<string, unknown>>).map(normalize);
  }
  return out;
}
